import React from 'react';
import { Bar as BarChart } from 'react-chartjs-2';
import { CheckCircle2, Globe, KeyRound, Lock, LogIn, MonitorSmartphone, ShieldAlert, XCircle } from 'lucide-react';
import { baseOptions } from '../lib/charts';
import { useApi } from '../lib/useApi';
import { ago, dateTime, num } from '../lib/format';
import { Card, Chip, Empty, ErrorBanner, PageHeader, PageSkeleton, Stat } from '../components/ui';

interface Resp {
  summary: { ok: number; failed: number; failed_ips: number; active_sessions: number; locked_accounts: number };
  attempts: { email: string; ip_address: string; user_agent: string; stage: string; success: boolean; reason: string | null; created_at: string }[];
  failures_by_ip: { ip_address: string; failures: number; last_at: string; emails: string[] }[];
  locked: { email: string; failures: number; last_at: string }[];
  sessions: { id: string; email: string; name: string; created_at: string; last_seen_at: string | null; expires_at: string; ip_address: string; user_agent: string; current: boolean }[];
  tenant_logins_14d: { day: string; logins: number }[];
  policy: { account_failures: number; lock_minutes: number };
}

const REASON: Record<string, string> = { bad_credentials: 'Wrong email or password', bad_pin: 'Wrong PIN', inactive: 'Account deactivated', no_pin: 'No PIN set' };
export const browserOf = (ua?: string) => {
  if (!ua) return 'Unknown device';
  const b = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : /curl|python|urllib/i.test(ua) ? 'Script' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${b} on ${os}` : b;
};

export default function Security() {
  const { data, error, loading, reload } = useApi<Resp>('/security', 30000);
  if (loading) return <PageSkeleton />;
  if (error || !data) return <ErrorBanner text={error || 'Could not load security data'} onRetry={reload} />;
  const s = data.summary;

  return (
    <div>
      <PageHeader title="Security" subtitle={`Admin sign-ins and sessions. Accounts lock for ${data.policy.lock_minutes} minutes after ${data.policy.account_failures} failed attempts.`} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Successful sign-ins" value={num(s.ok)} icon={CheckCircle2} tone="green" sub="Admin PIN step, last 7 days" />
        <Stat label="Failed attempts" value={num(s.failed)} icon={XCircle} tone={s.failed ? 'red' : 'slate'} sub={`from ${num(s.failed_ips)} IP address${s.failed_ips === 1 ? '' : 'es'} · 7 days`} />
        <Stat label="Locked accounts" value={num(s.locked_accounts)} icon={Lock} tone={s.locked_accounts ? 'amber' : 'slate'} sub="Right now" />
        <Stat label="Active admin sessions" value={num(s.active_sessions)} icon={MonitorSmartphone} tone="blue" sub="Signed-in browsers" />
      </div>

      {data.locked.length > 0 && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-900">
          <div className="mb-1 flex items-center gap-2 font-medium"><ShieldAlert className="h-4 w-4" />Locked right now</div>
          {data.locked.map(l => <div key={l.email}>{l.email} · {l.failures} failures · last {ago(l.last_at)}</div>)}
        </div>
      )}

      <div className="mt-5 grid gap-5 xl:grid-cols-[1.5fr_1fr]">
        <Card title="Recent admin sign-in attempts" icon={KeyRound} tone="blue" bodyClass="p-0">
          {data.attempts.length === 0 ? <Empty icon={KeyRound} title="No sign-in attempts yet" /> : (
            <div className="max-h-[440px] overflow-y-auto">
              <table className="min-w-full">
                <thead className="sticky top-0 border-b border-slate-100 bg-slate-50"><tr>
                  {['When', 'Account', 'Step', 'Result', 'From'].map(h => <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.attempts.map((a, i) => (
                    <tr key={i}>
                      <td className="whitespace-nowrap px-4 py-2.5 text-sm text-slate-600">{dateTime(a.created_at)}</td>
                      <td className="max-w-[200px] truncate px-4 py-2.5 text-sm text-slate-900">{a.email || '—'}</td>
                      <td className="px-4 py-2.5 text-sm capitalize text-slate-600">{a.stage.replace('_', ' ')}</td>
                      <td className="px-4 py-2.5">{a.success ? <Chip tone="green" dot>OK</Chip> : <Chip tone="red" dot>{REASON[a.reason || ''] || 'Failed'}</Chip>}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{a.ip_address}<br />{browserOf(a.user_agent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="space-y-5">
          <Card title="Failures by IP" icon={Globe} tone="red" right={<span className="text-xs text-slate-400">7 days</span>} bodyClass="p-0">
            {data.failures_by_ip.length === 0 ? <Empty icon={CheckCircle2} title="No failed attempts" /> : (
              <ul className="divide-y divide-slate-100">
                {data.failures_by_ip.map(f => (
                  <li key={f.ip_address} className="flex items-center justify-between gap-3 px-5 py-2.5">
                    <span className="min-w-0"><span className="block font-mono text-sm text-slate-900">{f.ip_address}</span>
                      <span className="block truncate text-xs text-slate-500">{f.emails.filter(Boolean).join(', ') || '—'} · last {ago(f.last_at)}</span></span>
                    <Chip tone={f.failures >= 10 ? 'red' : 'amber'}>{f.failures}</Chip>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card title="Tenant sign-ins" icon={LogIn} tone="teal" right={<span className="text-xs text-slate-400">14 days</span>}>
            {data.tenant_logins_14d.length === 0 ? <div className="text-sm text-slate-400">No tenant sign-ins recorded.</div> : (
              <div className="h-36"><BarChart data={{ labels: data.tenant_logins_14d.map(d => new Date(d.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
                datasets: [{ data: data.tenant_logins_14d.map(d => d.logins), backgroundColor: '#14b8a6', borderRadius: 4, maxBarThickness: 18 }] }} options={baseOptions()} /></div>
            )}
          </Card>
        </div>
      </div>

      <Card className="mt-5" title="Active admin sessions" icon={MonitorSmartphone} tone="blue" bodyClass="p-0">
        {data.sessions.length === 0 ? <Empty icon={MonitorSmartphone} title="No active sessions" /> : (
          <ul className="divide-y divide-slate-100">
            {data.sessions.map(se => (
              <li key={se.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-900">{se.name || se.email}{se.current && <Chip tone="green">This browser</Chip>}</span>
                  <span className="block text-xs text-slate-500">{browserOf(se.user_agent)} · {se.ip_address} · signed in {dateTime(se.created_at)}</span>
                </span>
                <span className="text-xs text-slate-500">Last active {ago(se.last_seen_at)} · ends {dateTime(se.expires_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
